import React, { useContext, useState, useEffect } from 'react';
import { Download, Filter, X, Search, Eye, Edit, Trash2, Plus, Ticket, Check, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import riskService from '../../../services/riskService';
import { useToast } from '../../../context/ToastContext';
import userService from '../../../services/userService';
import Pagination from '../../../components/Pagination';

export default function RiskManagement() {
  const { logoutUser,hasRole, user, authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    risk_name: '',
    probability: '',
    status: '',
    assigned_user: '',
    ordering: 'newest'
  });
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });
  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Fetch user's risks on component mount
  useEffect(() => {
    fetchMyRisks();
  }, [pagination.page, pagination.page_size]);

  const fetchMyRisks = async () => {
    setLoading(true);
    setError(null);
    
    try {

      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,
      };
      // console.log('🔍 ManageRistsk - Sending filters to backend:', filters);
      const result = await riskService.getMyRisks(authTokens.access, filters, pagination_data);
      
      if (result.success) {
        setRisks(result.data.results);

        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement des risques');
      }
    } catch (error) {
      console.error('Error fetching risks:', error);
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };



  const canAddRisk = () => {
    return  hasRole("RiskIdentifier");
  }
  // const canAddRisk = () => {
  //   return hasRole("RiskManager") || hasRole("RiskIdentifier");
  // }

  const hasEditPermissions =()=>{
    return hasRole("RiskManager") || hasRole("RiskIdentifier");
  }
  const canEditRisk = (risk) => {
    return hasEditPermissions() && risk.status.toLowerCase() != "approved";
  };

  const canDeleteRisk = () => {
    return  hasRole("Admin");
  }

  const canValidateRisk = (risk) => {
    return hasRole("RiskManager") && risk?.status?.toLowerCase() === "pending";
  }

  const canViewAllRisks = () => {
    return hasRole("Admin") || hasRole("RiskManager");
  }

  const probabiliteOptions = ['mineur', 'normal', 'majeur', 'critique'];

  const handlePageChange = (newPage) => {
      setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newPageSize) => {
      setPagination(prev => ({ 
        ...prev, 
        page_size: newPageSize, 
        page: 1 // Reset to first page
      }));
    };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      risk_name: '',
      probability: '',
      status: '',
      assigned_user: '',
      ordering: 'newest'
    });
    setUserSearchResults([]);
    setShowUserDropdown(false);

    setPagination(prev => ({ ...prev, page: 1 }));
    // Fetch will be triggered by useEffect when filters change
    setTimeout(() => fetchMyRisks(), 0);
  };

  const applyFilters = () => {
    setShowUserDropdown(false); // Close dropdown when applying filters
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchMyRisks(); // Re-fetch with current filters
  };

  const exportPDF = () => {
    console.log('Exporting to PDF...');
    // PDF export functionality
  };

  const handleShowFiltersSection = () => {
    setShowFiltersSection(!showFiltersSection);
    if (showFiltersSection) {
      setShowUserDropdown(false); // Close dropdown when hiding filters
    }
  };

  const handleUserSearch = async (e) => {
    const query = e.target.value;
    handleFilterChange('assigned_user', query);
    
    if (query.length >= 2) {
      setUserSearchLoading(true);
      try {
        const result = await userService.filterUsersBySearch(query, authTokens.access);
        if (result.success) {
          setUserSearchResults(result.data);
        } else {
          setUserSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching users:', error);
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    } else {
      setUserSearchResults([]);
    }
  };

  const selectUser = (user) => {
    handleFilterChange('assigned_user', user.email);
    setShowUserDropdown(false);
    setUserSearchResults([]);
  };

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.relative')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handleEditRisk = (risk) => {
    navigate(`/dashboard/risks/edit/${risk.id}`, { state: { risk } });
  };

  const handleViewRisk = (risk) => {
    navigate(`/dashboard/risks/${risk.id}`);
  };

  const handleDeleteRisk = async (riskId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce risque ?')) {
      try {
        const result = await riskService.deleteRisk(riskId, authTokens.access);
        
        if (result.success) {
          showSuccess('Risque supprimé avec succès');
          fetchMyRisks(); // Refresh the list
        } else {
          showError(result.error || 'Erreur lors de la suppression');
        }
      } catch (error) {
        console.error('Error deleting risk:', error);
        showError('Erreur de connexion');
      }
    }
  };

  const getProbabiliteBadge = (probabilite) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (probabilite?.toLowerCase()) {
      case 'critique':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'majeur':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      case 'normal':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'mineur':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status?.toLowerCase()) {
      case 'validé':
      case 'approved':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'rejeté':
      case 'rejected':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'en-cours':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'pending':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Since filtering is now done on the backend, we don't need client-side filtering
  const filteredRisks = risks;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Mes Risques</h2>

        <div className="flex flex-wrap gap-2">
          {canViewAllRisks() && (
            <Link
              to="/dashboard/risks-organisation-all"
              className="flex items-center px-3 py-1.5 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
            >
              Voir Tous
            </Link>
          )}
          <button
            onClick={handleShowFiltersSection}
            className="flex items-center px-3 py-1.5 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            <Filter className="h-3 w-3 mr-1.5" />
            Filtres
          </button>

          <button
            onClick={exportPDF}
            className="flex items-center px-3 py-1.5 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            <Download className="h-3 w-3 mr-1.5" />
            Export PDF
          </button>

          {canAddRisk() && (
            <Link
              to="/dashboard/risks/add"
              className="flex items-center px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <Plus className="h-3 w-3 mr-1.5" />
              Ajouter
            </Link>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={fetchMyRisks}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className='flex items-center justify-between mb-3'>
            <div className="flex items-center">
              <Filter className="h-4 w-4 text-gray-500 mr-2" />
              <h3 className="text-base font-medium text-gray-900">Filtres</h3>
            </div>
            <button onClick={handleShowFiltersSection}>
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            {/* Name Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nom du risque
              </label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  value={filters.risk_name}
                  onChange={(e) => handleFilterChange('risk_name', e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Probabilité Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Probabilité
              </label>
              <select
                value={filters.probability}
                onChange={(e) => handleFilterChange('probability', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Toutes</option>
                {probabiliteOptions.map(option => (
                  <option key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Tous</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvé</option>
                <option value="rejected">Rejeté</option>
              </select>
            </div>

            {/* Assigned User Filter */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Utilisateur assigné
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.assigned_user}
                  onChange={handleUserSearch}
                  onFocus={() => setShowUserDropdown(true)}
                  placeholder="Rechercher utilisateur..."
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent pr-8"
                />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                
                {/* User Dropdown */}
                {showUserDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {userSearchLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Recherche...</div>
                    ) : userSearchResults.length > 0 ? (
                      userSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => selectUser(user)}
                          className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{user.email}</div>
                          <div className="text-xs text-gray-500">
                            {user.first_name} {user.last_name}
                          </div>
                        </div>
                      ))
                    ) : filters.assigned_user ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Aucun utilisateur trouvé</div>
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">Tapez pour rechercher...</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Ordering Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Ordre
              </label>
              <select
                value={filters.ordering}
                onChange={(e) => handleFilterChange('ordering', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="newest">Plus récents</option>
                <option value="oldest">Plus anciens</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex space-x-2">
            <button
              onClick={applyFilters}
              className="flex items-center px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
            >
              <Filter className="h-3 w-3 mr-1.5" />
              Appliquer
            </button>
            <button
              onClick={clearFilters}
              className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              <X className="h-3 w-3 mr-1.5" />
              Effacer
            </button>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Probabilité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRisks.map((risk) => (
                <tr key={risk.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {risk.risk_name}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {risk.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getProbabiliteBadge(risk.probability)}>
                      {risk.probability}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(risk.risk_date).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(risk.status)}>
                      {risk.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button 
                      onClick={() => handleViewRisk(risk)}
                      className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                      title="Voir les détails"
                    >
                      <Eye className="h-4 w-4" />
                    </button>

                    {canEditRisk(risk) && (
                      <button 
                        onClick={() => handleEditRisk(risk)}
                        className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}

                    {/* {canDeleteRisk() && (
                      <button 
                        onClick={() => handleDeleteRisk(risk.id)}
                        className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button> 
                    )} */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="Risks" 
        />

        {/* Empty State */}
        {filteredRisks.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <p className="text-lg font-medium">Aucun risque trouvé</p>
              <p className="mt-1">
                {risks.length === 0 
                  ? "Vous n'avez créé aucun risque pour le moment." 
                  : "Essayez de modifier vos filtres."}
              </p>
              {canAddRisk() && risks.length === 0 && (
                <Link
                  to="/dashboard/risks/add"
                  className="inline-flex items-center mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Créer votre premier risque
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}